import { InstrumentTypeCode } from "../interfaces and Enums/ENUMInstrumentTypeCode";
import { StrategyType } from "../interfaces and Enums/ENUMStrategyType";
import { TradeType } from "../interfaces and Enums/ENUMTradeType";
import { IExecutionRequestCounterParties } from "../interfaces and Enums/InterfaceExecutionRequestCounterParties";
import { ExecutionRequestQuoteLeg } from "../interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import {
  ExecutionStrategy,
} from "../interfaces and Enums/InterfaceExecutionStrategy";
import { findCPOnTrade, findCPOnTradeByBlockId } from "../../components/widgets/tradeActionSummary/helper/tradeLookupHelpers";

interface IGenericTradeParams {
  data: any;
  currentField: any;
  fullTradeData: ExecutionStrategy;
  isES: boolean;
  isER: boolean;
  isLeg: boolean;
  isFill: boolean;
  isOp: boolean;
  isOutright: boolean;
  isFuture: undefined | boolean;
  isOrder: boolean;
  isFRN: undefined | boolean;
}

export function determineFullTradeData(params: any) {
  return params.colDef?.cellRendererParams?.fullTradeData
    ? params.colDef.cellRendererParams.fullTradeData
    : params.data; // if NO fullTradeData, component is looking at the ES level and can just use the data.
}

export function determineIfRoleIsOP(fullTradeData: any, data: any) {
  return fullTradeData ? fullTradeData.role === "OP" : data.role === "OP";
}

export function determineIsOutright(fullTradeData: any, data: any) {
  const isOutright = fullTradeData
    ? fullTradeData.strategyType == StrategyType.Outright
    : data.strategyType == StrategyType.Outright; // if NO fullTradeData, the params are from the Action Summary blotter.
  return isOutright;
}

export function determineIsER(params: any) {
  return params.data.executionRequestId != undefined;
}
export function determineIsLeg(params: any) {
  return params.data.blockId != undefined;
}
export function determineIsES(params: any) {
  return (
    params.colDef.cellRendererParams == undefined ||
    (params.data.role != undefined &&
      params.colDef.cellRendererParams.fullTradeData == undefined)
  ); // if NO fullTradeData, the params are from the Action Summary blotter.
}

export function determineIsFuture(
  isOp: boolean,
  isOutright: boolean,
  data: any,
) {
  let isFuture = undefined;
  if (!isOp) {
    if (isOutright) {
      if (data.legs?.length > 0) {
        isFuture = data.legs[0].instrumentType == InstrumentTypeCode.FUT;
      }
    } else {
      isFuture = data.instrumentType == InstrumentTypeCode.FUT;
    }
  }
  return isFuture;
}

export function determineIsFRN(
  isOp: boolean,
  isOutright: boolean,
  data: any,
) {
  let isFRN = undefined;
  if (isOutright) {
    if (data.legs?.length > 0) {
      isFRN = data.legs[0].instrumentType == InstrumentTypeCode.FRN;
    } else if (isOp && data.executionRequestCounterParties?.length > 0) {
      isFRN =
        data.executionRequestCounterParties[0].legs?.[0]?.instrumentType ==
        InstrumentTypeCode.FRN;
    }
  } else if (!isOp) {
    isFRN = data.instrumentType == InstrumentTypeCode.FRN;
  }
  return isFRN;
}

export function determineIsOrder(fullTradeData: any) {
  return fullTradeData.type == TradeType.Order;
}

export function determineIsFill(params: any) {
  return params.data?.rowType === "fill";
}

export function returnAllTradeDetailsFromGenericParams(
  params: any,
): IGenericTradeParams {
  const data = params.data;
  const currentField = params.colDef.field;

  const fullTradeData = determineFullTradeData(params);
  const isOp = determineIfRoleIsOP(fullTradeData, data);
  const isOutright = determineIsOutright(fullTradeData, data);
  // Trade Levels (table levels)
  const isER = determineIsER(params);
  const isLeg = determineIsLeg(params);
  const isFill = determineIsFill(params);
  const isES = determineIsES(params);
  // InstrumentTypeCode check
  const isFuture = determineIsFuture(isOp, isOutright, data);
  const isOrder = determineIsOrder(fullTradeData);
  const isFRN = determineIsFRN(isOp, isOutright, data);

  return {
    data,
    currentField,
    fullTradeData,
    isES,
    isER,
    isLeg,
    isFill,
    isOp,
    isOutright,
    isFuture,
    isOrder,
    isFRN
  };
}

// Function to return the data object for an OP ER - Using the executionRequestId
export function returnOPExecutionRequestFromThisTrade({
  params,
  thisTrade,
  isOp,
  isER,
  isOutright,
  // eslint-disable-next-line no-unused-vars
  isLeg,
}: {
  params: any;
  thisTrade: ExecutionStrategy;
  isOp: boolean;
  isER: boolean;
  isOutright: boolean;
  isLeg: boolean;
}): IExecutionRequestCounterParties | undefined {
  if (isOp) {
    if (isER) {
      if (isER && !isOutright) {
        const opERData: IExecutionRequestCounterParties = params.data;

        if (
          thisTrade &&
          thisTrade.executionRequestCounterParties &&
          thisTrade.executionRequestCounterParties.length > 0
        ) {
          const thisER = findCPOnTrade(thisTrade, opERData.executionRequestId);
          if (!thisER) {
            return undefined;
          }
          return thisER;
        }
      }
    }
  }
  return undefined;
}

export function returnOPLegFormThisTrade({
  params,
  thisTrade,
  isOp,
  // eslint-disable-next-line no-unused-vars
  isER,
  isOutright,
  isLeg,
}: {
  params: any;
  thisTrade: ExecutionStrategy;
  isOp: boolean;
  isER: boolean;
  isOutright: boolean;
  isLeg: boolean;
  isOrder?: boolean;
}): ExecutionRequestQuoteLeg | undefined {
  if (isOp) {
    if (isLeg || isOutright) {
      const opLegData: ExecutionRequestQuoteLeg = isOutright
        ? params.data?.legs?.[0]
        : params.data;

      if (
        thisTrade &&
        thisTrade.executionRequestCounterParties &&
        thisTrade.executionRequestCounterParties.length > 0
      ) {
        const thisER = findCPOnTradeByBlockId(thisTrade, opLegData?.blockId);
        if (!thisER) {
          return;
        }
        const thisLeg = thisER.legs.find(
          (leg: any) => leg.blockId == opLegData?.blockId,
        );

        if (!thisLeg) {
          return;
        }

        return thisLeg;
      }
    }
  }
  return undefined;
}

export function returnRPLegFromThisTrade({
  params,
  thisTrade,
  isOp,
  isOutright,
  isLeg,
}: {
  params: any;
  thisTrade: ExecutionStrategy;
  isOp: boolean;
  isOutright: boolean;
  isLeg: boolean;
}): ExecutionRequestQuoteLeg | undefined {
  if (!thisTrade) {
    return;
  }
  if (thisTrade && thisTrade.legs && thisTrade.legs.length > 0) {
    if (isLeg || isOutright) {
      if (!isOp) {
        // Confirm RP
        if (isOutright) {
          return thisTrade.legs[0];
        }
        // Structure:
        const thisLeg = thisTrade.legs.find(
          (leg) => leg.blockId == params.data.blockId,
        );
        if (thisLeg) {
          return thisLeg;
        }
      }
    }
  }
  return undefined;
}
